import { viewWalletAccount, viewWalletBalance } from "src/api/wallet";
import { buttonVariants } from "src/components/ui/button";
import { Copy, Plus, RefreshCcw } from "lucide-react";
import useHasPermissions from "src/hooks/useHasPermissions";
import CustomModal from "src/components/CustomModal";
import TopUpWallet from "src/components/modals/TopUpWallet";
import { copyToClipboard, renderCurrency, showSuccessToast } from "src/lib/utils";
import { useQueries } from "@tanstack/react-query";
import PermissionWrapper from "./PermissionWrapper";

const WalletDetails = () => {
	const canViewWalletBalance = useHasPermissions("view_wallet_balance");
	const canViewWalletAccount = useHasPermissions("view_wallet_account");

	const [{ data: rawWalletBalance, refetch }, { data: rawWalletAccount }] = useQueries({
		queries: [
			{
				queryKey: ["wallet-balance"],
				queryFn: viewWalletBalance,
				enabled: canViewWalletBalance,
				suspense: true,
			},
			{
				queryKey: ["wallet-account"],
				queryFn: viewWalletAccount,
				enabled: canViewWalletAccount,
				suspense: true,
			},
		],
	});

	const walletBalance = rawWalletBalance?.data;
	const walletAccount = rawWalletAccount?.data;

	const copyAccount = async (account_number) => {
		await copyToClipboard(account_number);
		showSuccessToast("Account number copied successfully");
	};

	return (
		<div className="tw-grid tw-gap-8 md:tw-grid-cols-2">
			<PermissionWrapper permission="view_wallet_balance">
				<div className="tw-flex tw-flex-wrap tw-gap-3">
					<div className="tw-py-4 tw-flex-1 tw-px-4 tw-gap-4 !tw-text-white tw-rounded-md tw-bg-green">
						<p className="tw-text-sm tw-mb-4 tw-font-semibold">Wallet Balance:</p>
						<p className="break-words whitespace-normal tw-text-3xl tw-font-semibold">{renderCurrency(walletBalance)}</p>
					</div>
					<div className="tw-grid tw-grid-rows-2 max-sm:tw-flex-1 tw-gap-3 tw-rounded-md">
						<CustomModal
							title="Top Up Wallet"
							trigger={
								<button
									className={buttonVariants({
										variant: "default",
										className: "!tw-px-6 tw-gap-2 !tw-py-3 tw-text-[13px] tw-font-semibold",
									})}
									onClick={() => refetch()}
								>
									<Plus className="tw-size-4" /> Top Up
								</button>
							}
						>
							<TopUpWallet />
						</CustomModal>
						<button
							className={buttonVariants({
								variant: "secondary",
								className: "!tw-px-6 tw-gap-2 !tw-py-3 tw-text-[13px] tw-font-semibold",
							})}
							onClick={() => refetch()}
						>
							<RefreshCcw className="tw-size-4" /> Refresh
						</button>
					</div>
				</div>
			</PermissionWrapper>
			<PermissionWrapper permission="view_wallet_account">
				<div className="tw-bg-white tw-py-4 tw-rounded-md tw-shadow-md tw-px-4">
					<p className="tw-text-sm tw-font-semibold">Account details</p>
					<p className="tw-text-sm">
						Account number:{" "}
						<span className="tw-inline-flex tw-gap-2 tw-items-center tw-font-semibold">
							{walletAccount?.account_number}
							<button
								className={buttonVariants({ variant: "default", className: "!tw-px-2 !tw-py-2" })}
								onClick={async () => await copyAccount(walletAccount?.account_number)}
							>
								<Copy className="tw-size-4" />
							</button>
						</span>
					</p>
					<p className="tw-text-sm">
						Account name: <span className="tw-font-semibold">{walletAccount?.account_name}</span>
					</p>
					<p className="tw-text-sm">
						Bank name: <span className="tw-font-semibold">{walletAccount?.bank_name}</span>
					</p>
				</div>
			</PermissionWrapper>
		</div>
	);
};

export default WalletDetails;
